/* Social Proof — custom section behavior.
   Category filtering (via the theme's scroll-carousel filter API),
   read-more expansion with operator-editable labels, mobile-only ambient
   autoplay, per-card sound toggle (one unmuted video at a time), and
   dead-source detection with an editor-only warning badge. */

if (!window.customElements.get('social-proof-section')) {
  class SocialProofSection extends HTMLElement {
    connectedCallback() {
      this.carousel = this.querySelector('scroll-carousel');
      this.cards = this.carousel ? Array.from(this.carousel.querySelectorAll('[data-review-category]')) : [];
      this.videoCards = this.cards.filter((card) => card.querySelector('.social-proof__card-media video, .social-proof__card-media mux-video'));

      this.loadMuxEngineIfNeeded();

      this.mobileQuery = window.matchMedia('(max-width: 699px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.setupFilters();
      this.setupReadMore();
      this.setupSoundToggles();
      this.setupVideoErrorGuard();
      this.setupViewportObserver();

      this.onMobileChange = () => this.syncAmbientState();
      this.mobileQuery.addEventListener('change', this.onMobileChange);
    }

    disconnectedCallback() {
      if (this.viewObserver) this.viewObserver.disconnect();
      this.mobileQuery.removeEventListener('change', this.onMobileChange);
    }

    get ambientEnabled() {
      return this.hasAttribute('data-autoplay') && this.mobileQuery.matches && !this.reducedMotion;
    }

    videoOf(card) {
      return card.querySelector('.social-proof__card-media video, .social-proof__card-media mux-video');
    }

    /* Load Mux's video engine only when this page actually has a Mux card. */
    loadMuxEngineIfNeeded() {
      if (!this.querySelector('mux-video')) return;
      if (window.customElements.get('mux-video') || document.querySelector('script[data-mux-video-lib]')) return;

      const script = document.createElement('script');
      script.src = this.dataset.muxLib;
      script.defer = true;
      script.setAttribute('data-mux-video-lib', '');
      document.head.appendChild(script);
    }

    toggleOf(card) {
      return card.querySelector('[data-sound-toggle]');
    }

    /* ── Filters ─────────────────────────────────────────────────────── */

    setupFilters() {
      this.querySelectorAll('[data-filter-category]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const category = btn.getAttribute('data-filter-category');

          this.querySelectorAll('[data-filter-category]').forEach((b) => {
            b.classList.toggle('is-active', b === btn);
          });

          if (!this.carousel) return;

          if (category === 'all') {
            this.carousel.filter([]);
          } else {
            const hidden = [];
            this.cards.forEach((card, index) => {
              if (card.getAttribute('data-review-category') !== category) {
                hidden.push(index);
              }
            });
            this.carousel.filter(hidden);
          }

          this.carousel.select(0, { instant: true });
        });
      });
    }

    /* ── Read more ───────────────────────────────────────────────────── */

    setupReadMore() {
      const readMoreLabel = this.dataset.readMoreLabel || 'read more';
      const showLessLabel = this.dataset.showLessLabel || 'show less';

      this.querySelectorAll('[data-read-more]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const card = btn.closest('.social-proof__card');
          const textEl = card && card.querySelector('.social-proof__card-text');
          if (!textEl) return;

          const isExpanded = textEl.classList.toggle('is-expanded');
          btn.textContent = isExpanded ? showLessLabel : readMoreLabel;
        });
      });
    }

    /* ── Sound toggle ────────────────────────────────────────────────── */

    setupSoundToggles() {
      this.videoCards.forEach((card) => {
        const btn = this.toggleOf(card);
        const video = this.videoOf(card);
        if (!btn || !video) return;

        btn.addEventListener('click', () => {
          if (typeof video.play !== 'function') return;
          if (video.paused) {
            /* Start this card with sound; silence every other card */
            this.muteAllExcept(card);
            video.muted = false;
            this.safePlay(video);
          } else if (video.muted) {
            /* Ambient playback → give it sound */
            this.muteAllExcept(card);
            video.muted = false;
          } else {
            /* Unmuted → back to ambient (mobile) or stopped (desktop) */
            video.muted = true;
            if (!this.ambientEnabled) video.pause();
          }

          this.updateToggle(card);
        });

        ['play', 'pause', 'volumechange'].forEach((evt) => {
          video.addEventListener(evt, () => this.updateToggle(card));
        });
      });
    }

    muteAllExcept(exceptCard) {
      this.videoCards.forEach((card) => {
        if (card === exceptCard) return;
        const video = this.videoOf(card);
        if (!video.muted) {
          video.muted = true;
          if (!this.ambientEnabled) video.pause();
        }
        this.updateToggle(card);
      });
    }

    updateToggle(card) {
      const btn = this.toggleOf(card);
      const video = this.videoOf(card);
      if (!btn || !video) return;
      btn.classList.toggle('is-playing', !video.paused);
      btn.classList.toggle('is-unmuted', !video.paused && !video.muted);
    }

    safePlay(video) {
      const p = video.play();
      if (p) p.catch(() => {});
    }

    /* ── Viewport behavior ───────────────────────────────────────────── */

    setupViewportObserver() {
      if (this.videoCards.length === 0) return;

      this.viewObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const card = entry.target;
            const video = this.videoOf(card);
            if (!video || typeof video.play !== 'function') return;

            if (entry.intersectionRatio >= 0.5) {
              if (this.ambientEnabled && video.paused && video.muted) {
                this.safePlay(video);
              }
            } else if (!video.paused) {
              /* Off-screen: stop and reset to ambient state */
              video.pause();
              video.muted = true;
              this.updateToggle(card);
            }
          });
        },
        { threshold: [0, 0.5] }
      );

      this.videoCards.forEach((card) => this.viewObserver.observe(card));
    }

    syncAmbientState() {
      this.videoCards.forEach((card) => {
        const video = this.videoOf(card);
        if (!this.ambientEnabled && !video.paused && video.muted) {
          video.pause();
          this.updateToggle(card);
        }
      });
    }

    /* ── Dead-source guard ───────────────────────────────────────────── */

    setupVideoErrorGuard() {
      const designMode = this.hasAttribute('data-design-mode');

      this.videoCards.forEach((card) => {
        const video = this.videoOf(card);

        video.addEventListener('error', () => {
          card.classList.add('sp-video-error');

          if (designMode && !card.querySelector('.social-proof__video-warning')) {
            const warning = document.createElement('div');
            warning.className = 'social-proof__video-warning';
            warning.textContent = 'Video source missing — check the Mux playback ID and enable MP4 support (static renditions) on the asset.';
            card.appendChild(warning);
          }
        }, true);

        /* In the editor, surface dead sources immediately without waiting
           for playback (desktop has no ambient autoplay). Mux elements
           upgrade after their engine script loads, so wait for that. */
        if (designMode) {
          const probe = () => {
            if (typeof video.load !== 'function') return;
            video.preload = 'metadata';
            video.load();
          };
          if (video.tagName === 'MUX-VIDEO' && !window.customElements.get('mux-video')) {
            window.customElements.whenDefined('mux-video').then(probe);
          } else {
            probe();
          }
        }
      });
    }
  }

  window.customElements.define('social-proof-section', SocialProofSection);
}
